import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Subscription } from 'rxjs';
import { DialogSeguimientoCotizacionComponent } from '../common/dialogs/dialog-seguimiento-cotizacion/dialog-seguimiento-cotizacion.component';
import { NotificacionesService } from '../../services/notificaciones/notificaciones-cotizacion.service';
import { NetworkingMyQuotation } from '../../services/mis-cotizaciones/miscotizaciones.service';
import { AuthService } from '../../services/security/auth.service';
import { NotificacionCotizacionDTO } from '../../models/notificaciones/notificacion-cotizacion.model';

@Component({
  selector: 'app-notificaciones',
  templateUrl: './notificaciones.component.html',
  styleUrls: ['./notificaciones.component.css']
})
export class NotificacionesComponent implements OnInit, OnDestroy {

  notificaciones: NotificacionCotizacionDTO[] = [];
  notificacionesFiltradas: NotificacionCotizacionDTO[] = [];
  notificacionesPagina: NotificacionCotizacionDTO[] = [];
  filtroActivo: 'todas' | 'no_leidas' | 'leidas' = 'todas';
  cargando = false;
  pageIndex = 0;
  pageSize = 10;

  private idAsesor: number | null = null;
  private cedulaAsesor = '';
  private resumenSubscription: Subscription = new Subscription();

  constructor(
    private router: Router,
    private location: Location,
    private dialog: MatDialog,
    private notificacionesService: NotificacionesService,
    private networking: NetworkingMyQuotation,
    private authService: AuthService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.obtenerIdAsesor();
    if (this.idAsesor) {
      this.cargarNotificaciones();
    }
  }

  ngOnDestroy(): void {
    this.resumenSubscription.unsubscribe();
  }

  private async obtenerIdAsesor(): Promise<void> {
    try {
      const authInfo = await this.authService.getAuthInfo();
      this.cedulaAsesor = authInfo.document.split('.')[1].trim();
      this.idAsesor = await this.networking.obtenerIdAsesorPorDocumento(this.cedulaAsesor);
    } catch (error) {
      console.error('Error al obtener el idAsesor:', error);
    }
  }

  private cargarNotificaciones(): void {
    this.cargando = true;
    // Suscribirse al BehaviorSubject para que la lista se actualice automáticamente
    // cuando VER o ELIMINAR respondan del backend
    this.resumenSubscription = this.notificacionesService.resumen$.subscribe(resumen => {
      if (resumen) {
        this.notificaciones = resumen.notificaciones;
        this.aplicarFiltro();
        this.cargando = false;
      }
    });

    // Llamada inicial al backend; el tap del servicio emite en el BehaviorSubject
    this.notificacionesService.getResumen(this.idAsesor!.toString()).subscribe({
      error: () => { this.cargando = false; }
    });
  }

  volver(): void {
    this.location.back();
  }

  getIconoNotificacion(notif: NotificacionCotizacionDTO): string {
    if (notif.idTipoAlerta === 2) return 'warning';
    if (notif.idTipoAlerta === 3) return 'check_circle';
    return 'info';
  }

  getClasesNotificacion(notif: NotificacionCotizacionDTO): { [key: string]: boolean } {
    return {
      'notificacion-info': notif.idTipoAlerta === 1,
      'notificacion-error': notif.idTipoAlerta === 2,
      'notificacion-success': notif.idTipoAlerta === 3,
      'notificacion-leida': notif.leida,
    };
  }

  formatearFecha(fechaISO: string): string {
    if (!fechaISO) return '';
    const d = new Date(fechaISO);
    const dia = d.getDate().toString().padStart(2, '0');
    const mes = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${dia}/${mes}/${d.getFullYear()}`;
  }

  verNotificacion(notif: NotificacionCotizacionDTO): void {
    if (!this.idAsesor) return;
    this.notificacionesService.marcarLeida(notif.idNotificacion, this.idAsesor.toString()).subscribe({
      next: () => {
        this.dialog.open(DialogSeguimientoCotizacionComponent, {
          width: '900px',
          maxWidth: '95vw',
          data: {
            idCotizacion: notif.idCotizacion,
            numeroCotizacion: notif.idCotizacion.toString(),
            nombreCliente: this.extractNombreCliente(notif.mensaje),
          }
        });
      },
      error: (err) => console.error('Error al marcar notificación como leída:', err)
    });
  }

  eliminarNotificacion(notif: NotificacionCotizacionDTO, event: Event): void {
    event.stopPropagation();
    if (!this.idAsesor) return;
    this.notificacionesService.eliminar(notif.idNotificacion, this.idAsesor.toString()).subscribe({
      error: (err) => console.error('Error al eliminar notificación:', err)
    });
  }

  cambiarFiltro(filtro: 'todas' | 'no_leidas' | 'leidas'): void {
    this.filtroActivo = filtro;
    this.pageIndex = 0;
    this.aplicarFiltro();
  }

  aplicarFiltro(): void {
    switch (this.filtroActivo) {
      case 'no_leidas':
        this.notificacionesFiltradas = this.notificaciones.filter(n => !n.leida);
        break;
      case 'leidas':
        this.notificacionesFiltradas = this.notificaciones.filter(n => n.leida);
        break;
      default:
        this.notificacionesFiltradas = [...this.notificaciones];
    }
    this.recortarPagina();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.recortarPagina();
  }

  private recortarPagina(): void {
    const maxPage = Math.max(0, Math.ceil(this.notificacionesFiltradas.length / this.pageSize) - 1);
    if (this.pageIndex > maxPage) {
      this.pageIndex = maxPage;
    }
    const start = this.pageIndex * this.pageSize;
    this.notificacionesPagina = this.notificacionesFiltradas.slice(start, start + this.pageSize);
  }

  getNotificacionesNoLeidas(): number {
    return this.notificaciones.filter(n => !n.leida).length;
  }

  private extractNombreCliente(mensaje: string): string {
    const match = mensaje.match(/del usuario ([^,]+?)(?:,|\s+con|\s+venció)/);
    return match ? match[1].trim() : '';
  }
}

